import React, { useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useTranslation } from "react-i18next";
import styles from "./AudioVolumeSlider.module.css";

interface VolumeSliderProps {
    initialValue?: number;
    min?: number;
    max?: number;
    step?: number;
    onChange?: (value: number) => void;
    width?: number | string;
    hideLabels?: boolean;
    fractionDigits?: number;
    disabled?: boolean;
}

const VolumeSlider: React.FC<VolumeSliderProps> = ({
    initialValue = 0,
    min = -12,
    max = 12,
    step = 1,
    onChange,
    width = 300,
    hideLabels = false,
    fractionDigits = 0,
    disabled = false,
}) => {
    const [value, setValue] = useState<number>(initialValue);
    const [inputValue, setInputValue] = useState<string>(initialValue.toFixed(fractionDigits));

    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const handleSliderChange = (val: number | number[]) => {
        const newValue = Array.isArray(val) ? val[0] : val;
        setValue(newValue);
        setInputValue(newValue.toFixed(fractionDigits));
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        const numericValue = parseFloat(newValue);
        if (!isNaN(numericValue) && numericValue >= min && numericValue <= max) {
            setValue(numericValue);
            if (onChange) {
                onChange(numericValue);
            }
        }
    };

    const handleInputBlur = () => {
        const numericValue = parseFloat(inputValue);
        if (isNaN(numericValue) || numericValue < min || numericValue > max) {
            setInputValue(value.toFixed(fractionDigits));
        }
    };

    return (
        <div className={`${styles.container}${disabled ? ` ${styles.disabled}` : ""}`} style={{ width }}>
            {!hideLabels && (
                <div className={styles.labels}>
                    <label>{t("quieter")}</label>
                    <label>{t("louder")}</label>
                </div>
            )}
            <div className={styles.inputContainer}>
                <div className={styles.track}>
                    <Slider
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={handleSliderChange}
                        disabled={disabled}
                        railStyle={{ backgroundColor: "#ddd", height: 6 }}
                        trackStyle={{ backgroundColor: "#86E5D0", height: 6 }}
                        handleStyle={{
                            borderColor: "#86E5D0",
                            height: 16,
                            width: 16,
                            marginTop: -5,
                            backgroundColor: "#fff",
                        }}
                    />
                </div>
                <div className={styles.valueContainer}>
                    <input
                        type="number"
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        step={step}
                        min={min}
                        max={max}
                        disabled={disabled}
                        className={styles.numberInput}
                    />
                    <span className={styles.suffix}>%</span>
                </div>
            </div>
        </div>
    );
};

export default VolumeSlider;
